"use client";

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Trash2, RefreshCw, Search, Star } from "lucide-react";

interface MemoryEntry {
  id: string;
  content: string;
  tags: string[];
  timestamp: number;
  durable: boolean;
}

interface MindalooMemoryPanelProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
}

export default function MindalooMemoryPanel({ open, onClose, userId = "local" }: MindalooMemoryPanelProps) {
  const [memories, setMemories] = useState<MemoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchMemories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/karma-ac/memories?userId=${encodeURIComponent(userId)}&limit=50`);
      if (res.ok) {
        const data = await res.json();
        setMemories(data.memories ?? []);
      }
    } catch {
      // Supabase not connected yet — show empty state gracefully
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) fetchMemories();
  }, [open, fetchMemories]);

  const deleteMemory = async (id: string) => {
    setDeleting(id);
    try {
      await fetch(`/api/karma-ac/memories?id=${id}`, { method: "DELETE" });
      setMemories(prev => prev.filter(m => m.id !== id));
    } catch {
      // silent
    } finally {
      setDeleting(null);
    }
  };

  const filtered = memories.filter(m =>
    filter ? m.content.toLowerCase().includes(filter.toLowerCase()) ||
              m.tags.some(t => t.toLowerCase().includes(filter.toLowerCase()))
           : true
  );

  const fmt = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1540] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 32 }}
            className="fixed right-0 top-0 bottom-0 w-[360px] z-[1550] karma-glass flex flex-col"
            style={{ borderLeft: "1px solid rgba(193,162,255,0.2)" }}
          >
            {/* Header */}
            <div className="px-5 pt-6 pb-4 border-b border-white/10 flex items-center gap-3">
              <Brain className="w-5 h-5 text-[#C1A2FF]" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Karma's Memory</p>
                <p className="text-[10px] text-white/40">{memories.length} stored {memories.filter(m => m.durable).length} durable</p>
              </div>
              <button type="button" onClick={fetchMemories} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Refresh">
                <RefreshCw className={`w-3.5 h-3.5 text-white/50 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/50 text-lg leading-none">×</button>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-2 karma-glass-subtle rounded-xl px-3 py-2">
                <Search className="w-3.5 h-3.5 text-white/30 shrink-0" />
                <input
                  className="bg-transparent text-xs text-white placeholder-white/30 outline-none flex-1"
                  placeholder="Search memories..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                />
              </div>
            </div>

            {/* Memory list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {loading && (
                <div className="text-center text-white/30 text-xs py-8">Loading...</div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="text-center py-12">
                  <Brain className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 text-xs">
                    {memories.length === 0 ? "No memories yet — chat with Karma to build them." : "No matches."}
                  </p>
                </div>
              )}
              {filtered.map(m => (
                <motion.div
                  key={m.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="karma-glass-subtle rounded-xl p-3 group"
                >
                  <div className="flex items-start gap-2">
                    {m.durable && <Star className="w-3 h-3 text-yellow-400 shrink-0 mt-0.5" />}
                    <p className="text-xs text-white/85 leading-relaxed flex-1">{m.content}</p>
                    <button
                      type="button"
                      onClick={() => deleteMemory(m.id)}
                      disabled={deleting === m.id}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-500/20"
                    >
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                  {m.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {m.tags.map(tag => (
                        <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#C1A2FF]/15 text-[#C1A2FF]/80">{tag}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-[9px] text-white/25 mt-1.5">{fmt(m.timestamp)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
